import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CompanyDto } from '@/services/ws-companies/src/main/resources/rest-api';
import { createContexts } from '@/js/libs/create-contexts';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSearchParams } from '@/js/libs/router';
import { useNotification } from '@/js/libs/ui/src/V2';

import useLocalStorage, { StorageKey as LocalStorageKey } from 'src/hooks/useLocalStorage.ts';
import { useCleanupSessionStorageItems } from './useCleanupSessionStorageItems.ts';

export type CompanyIdentifiersFragment = Pick<CompanyDto, 'id' | 'companyUUID' | 'name' | 'temporaryAccessRequired'>;

const { useCompanySwitchContext } = createContexts({
  CompanySwitch() {
    const { saveJSON, removeItem, loadJSON, loadNumber, saveNumber } = useLocalStorage();
    const cleanupSessionStorageItems = useCleanupSessionStorageItems();

    const [t] = useTranslation();
    const notify = useNotification();

    const [, setSearchParams] = useSearchParams();

    const [company, setCompany] = useState<CompanyIdentifiersFragment | null>(() =>
      loadJSON(LocalStorageKey.COMPANY, (e) => {
        // Defer notification to after render completes
        queueMicrotask(() => {
          notify.error({
            body: t((e as Error).message),
          });
        });
        // Also clear the corrupted data to prevent future issues
        removeItem(LocalStorageKey.COMPANY);
      }),
    );

    const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(() => loadNumber(LocalStorageKey.TEMPORARY_ACCESS_EXPIRES_AT));

    const handleCompanyChange = useCallbackRef((option: CompanyIdentifiersFragment | null, nextSessionExpiresAt: number | null = null) => {
      setSessionExpiresAt(nextSessionExpiresAt);
      if (nextSessionExpiresAt) {
        saveNumber(LocalStorageKey.TEMPORARY_ACCESS_EXPIRES_AT, nextSessionExpiresAt);
      } else {
        removeItem(LocalStorageKey.TEMPORARY_ACCESS_EXPIRES_AT);
      }

      if (option) {
        setCompany(option);
        try {
          saveJSON(LocalStorageKey.COMPANY, {
            id: option.id,
            companyUUID: option.companyUUID,
            name: option.name,
            temporaryAccessRequired: option.temporaryAccessRequired,
          });
        } catch (e) {
          notify.error({ body: t((e as Error).message) });
        }
      } else {
        setCompany(null);
        removeItem(LocalStorageKey.COMPANY);
      }

      // Let listeners finish their job
      setTimeout(() => {
        setSearchParams(undefined);
        cleanupSessionStorageItems();
      }, 0);
    });

    return {
      company,
      sessionExpiresAt,
      onCompanyChange: handleCompanyChange,
    };
  },
});

const CompanySwitchProvider = useCompanySwitchContext.Provider;

export { useCompanySwitchContext, CompanySwitchProvider };
